// Marketing Director — campaigns, strategy versions, and the brief review flow
// (approve / request changes → Phase 3 backend).
import { useCallback, useEffect, useState } from "react";
import { marketingFetch } from "../lib/api";

export function MarketingCampaigns({ facilityId }) {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    goal: "",
    budget: "",
    kill: "",
  });
  const [segCount, setSegCount] = useState(null);
  const [changeNotes, setChangeNotes] = useState("");

  const load = useCallback(() => {
    marketingFetch(`/campaigns?facility_id=${facilityId}`).then(
      (r) => r.ok && setCampaigns(r.data.campaigns || []),
    );
  }, [facilityId]);
  useEffect(load, [load]);

  const loadVersions = useCallback(
    (cid) => {
      marketingFetch(
        `/campaigns/${cid}/strategy/versions?facility_id=${facilityId}`,
      ).then((r) => r.ok && setVersions(r.data.versions || []));
    },
    [facilityId],
  );
  useEffect(() => {
    if (selected) loadVersions(selected.id);
  }, [selected, loadVersions]);

  useEffect(() => {
    if (!creating) return;
    marketingFetch(`/wizard/segment-count`, {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId, filters: {} }),
    }).then((r) => r.ok && setSegCount(r.data));
  }, [creating, facilityId]);

  const showError = (r, fallback) => {
    const d = r.data?.detail;
    if (d?.unmet)
      setError(
        `Onboarding incomplete — unmet: ${d.unmet.join(", ")} (see Setup tab)`,
      );
    else if (d?.issues) setError(d.issues.join("; "));
    else setError(typeof d === "string" ? d : fallback);
  };

  const createCampaign = async () => {
    setBusy(true);
    setError(null);
    const r = await marketingFetch(`/campaigns`, {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        name: draft.name,
        goal: draft.goal,
        budget_cents: draft.budget
          ? Math.round(Number(draft.budget) * 100)
          : null,
        kill_threshold_cents: draft.kill
          ? Math.round(Number(draft.kill) * 100)
          : null,
      }),
    });
    setBusy(false);
    if (!r.ok) return showError(r, "Could not create campaign");
    setCreating(false);
    setDraft({ name: "", goal: "", budget: "", kill: "" });
    load();
  };

  const generate = async (cid, notes) => {
    setBusy(true);
    setError(null);
    const r = await marketingFetch(`/campaigns/${cid}/strategy/generate`, {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        notes: notes || undefined,
      }),
    });
    setBusy(false);
    if (!r.ok) return showError(r, "Generation failed");
    loadVersions(cid);
  };

  const approveVersion = async (vid) => {
    setBusy(true);
    setError(null);
    const r = await marketingFetch(`/strategy/versions/${vid}/approve`, {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId }),
    });
    setBusy(false);
    if (!r.ok) return showError(r, "Approval blocked");
    load();
    if (selected) loadVersions(selected.id);
  };

  const requestChanges = async (vid) => {
    if (!changeNotes.trim())
      return setError("Add notes describing the changes you want.");
    setBusy(true);
    setError(null);
    const r = await marketingFetch(
      `/strategy/versions/${vid}/request-changes`,
      {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId, notes: changeNotes }),
      },
    );
    setBusy(false);
    if (!r.ok) return showError(r, "Request failed");
    setChangeNotes("");
    if (selected) loadVersions(selected.id);
  };

  const setStatus = async (cid, status) => {
    const r = await marketingFetch(`/campaigns/${cid}`, {
      method: "PATCH",
      body: JSON.stringify({ facility_id: facilityId, status }),
    });
    if (!r.ok) showError(r, "Status change failed");
    load();
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
          <button className="ml-3 underline" onClick={() => setError(null)}>
            dismiss
          </button>
        </div>
      )}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-700">Campaigns</h3>
        <button
          onClick={() => setCreating((v) => !v)}
          className="px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm hover:bg-red-800"
        >
          {creating ? "Cancel" : "New campaign"}
        </button>
      </div>

      {creating && (
        <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-600">
              Name
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300"
              />
            </label>
            <label className="text-sm text-slate-600">
              What does success look like?
              <input
                value={draft.goal}
                onChange={(e) => setDraft({ ...draft, goal: e.target.value })}
                placeholder="e.g. 10 discovery calls booked"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300"
              />
            </label>
            <label className="text-sm text-slate-600">
              Budget ($, optional)
              <input
                value={draft.budget}
                onChange={(e) => setDraft({ ...draft, budget: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300"
              />
            </label>
            <label className="text-sm text-slate-600">
              Kill threshold ($, optional — spend past this auto-pauses the
              campaign)
              <input
                value={draft.kill}
                onChange={(e) => setDraft({ ...draft, kill: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300"
              />
            </label>
          </div>
          {segCount && (
            <div className="text-sm text-slate-600">
              Segment: <b>{segCount.total}</b> active prospects,{" "}
              {segCount.sms_consented} with SMS consent.
              {segCount.warning && (
                <span className="text-slate-500"> {segCount.warning}</span>
              )}
            </div>
          )}
          <button
            disabled={busy || !draft.name}
            onClick={createCampaign}
            className="px-4 py-2 rounded-lg bg-red-700 text-white text-sm disabled:opacity-50"
          >
            Create
          </button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {campaigns.map((c) => (
          <div key={c.id}>
            <button
              onClick={() => setSelected(selected?.id === c.id ? null : c)}
              className="w-full text-left px-4 py-3 flex justify-between items-center hover:bg-slate-50"
            >
              <span className="font-medium text-slate-800">{c.name}</span>
              <span className="text-sm text-slate-500">{c.status}</span>
            </button>
            {selected?.id === c.id && (
              <div className="px-4 pb-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={busy}
                    onClick={() => generate(c.id)}
                    className="px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm disabled:opacity-50"
                  >
                    {versions.length
                      ? "Regenerate strategy"
                      : "Generate strategy"}
                  </button>
                  {c.status === "approved" && (
                    <button
                      onClick={() => setStatus(c.id, "active")}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700"
                    >
                      Activate
                    </button>
                  )}
                  {c.status === "active" && (
                    <button
                      onClick={() => setStatus(c.id, "paused")}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700"
                    >
                      Pause
                    </button>
                  )}
                  {c.status === "paused" && (
                    <button
                      onClick={() => setStatus(c.id, "active")}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700"
                    >
                      Resume
                    </button>
                  )}
                </div>
                {versions.map((v) => (
                  <div
                    key={v.id}
                    className="rounded-lg border border-slate-200"
                  >
                    <div className="px-3 py-2 flex justify-between items-center bg-slate-50 rounded-t-lg">
                      <span className="text-sm text-slate-700">
                        v{v.version} · <b>{v.status}</b>
                        {v.validation?.issues?.length > 0 && (
                          <span className="text-red-700 ml-2">
                            {v.validation.issues.length} issue(s)
                          </span>
                        )}
                        {v.validation?.flags?.length > 0 && (
                          <span className="text-slate-500 ml-2">
                            {v.validation.flags.length} flag(s)
                          </span>
                        )}
                      </span>
                      {v.status === "draft" && (
                        <div className="flex gap-2 items-center">
                          <button
                            disabled={busy}
                            onClick={() => approveVersion(v.id)}
                            className="px-3 py-1 rounded bg-red-700 text-white text-xs disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => requestChanges(v.id)}
                            className="px-3 py-1 rounded border border-slate-300 text-xs text-slate-700"
                          >
                            Request changes
                          </button>
                        </div>
                      )}
                    </div>
                    {v.status === "draft" && (
                      <div className="px-3 pt-2">
                        <input
                          value={changeNotes}
                          onChange={(e) => setChangeNotes(e.target.value)}
                          placeholder="Notes for requested changes"
                          className="w-full px-3 py-1.5 rounded-lg border border-slate-300 text-sm"
                        />
                      </div>
                    )}
                    {(v.validation?.issues || []).map((i, n) => (
                      <div key={n} className="px-3 pt-1 text-xs text-red-700">
                        {i}
                      </div>
                    ))}
                    {(v.validation?.flags || []).map((f, n) => (
                      <div key={n} className="px-3 pt-1 text-xs text-slate-500">
                        {f}
                      </div>
                    ))}
                    <pre className="p-3 text-sm text-slate-600 whitespace-pre-wrap font-sans">
                      {v.brief_md || "(no brief)"}
                    </pre>
                  </div>
                ))}
                {!versions.length && (
                  <div className="text-sm text-slate-400">
                    No strategy versions yet.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!campaigns.length && (
          <div className="p-6 text-slate-400 text-sm">No campaigns yet.</div>
        )}
      </div>
    </div>
  );
}
